import { ChangePasswordDialog } from '@/components/auth/ChangePasswordDialog';
import { useTheme } from '@/components/theme/ThemeProvider';
import { useAuth } from '@/lib/auth';
import { useBuildVersion } from '@/lib/buildVersion';
import { cn } from '@/lib/cn';
import { useNavigate } from '@tanstack/react-router';
import {
  Cable,
  ChevronsUpDown,
  KeyRound,
  LogOut,
  Monitor,
  Moon,
  Shield,
  Sun,
  UsersRound,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return trimmed.slice(0, 1).toUpperCase();
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '?') + (last[0] ?? '?')).toUpperCase();
}

export function UserChip({ allowThemeToggle = true }: { allowThemeToggle?: boolean }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const buildInfo = useBuildVersion();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user) return null;

  const roleLabel = user.role === 'admin' ? 'Admin' : 'User';

  const onLogout = async () => {
    setOpen(false);
    await logout();
    navigate({ to: '/login' });
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-surface-muted/80 transition-colors text-left"
      >
        <span className="size-8 rounded-full bg-accent text-accent-foreground grid place-items-center text-xs font-semibold shrink-0">
          {initials(user.displayName || user.email)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium truncate">{user.displayName}</span>
          <span className="block text-xs text-foreground-muted truncate">{roleLabel}</span>
        </span>
        <ChevronsUpDown className="size-3.5 text-foreground-muted shrink-0" />
      </button>

      {open ? (
        <div className="absolute bottom-full mb-2 left-0 right-0 bg-surface border border-border rounded-lg shadow-lg py-1 z-50">
          {allowThemeToggle ? (
            <>
              <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider text-foreground-muted">
                테마
              </div>
              {(
                [
                  { key: 'light', label: '라이트', icon: Sun },
                  { key: 'dark', label: '다크', icon: Moon },
                  { key: 'system', label: '시스템', icon: Monitor },
                ] as const
              ).map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTheme(key)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-1.5 text-sm hover:bg-surface-muted',
                    theme === key ? 'text-foreground' : 'text-foreground-muted',
                  )}
                >
                  <Icon className="size-4" />
                  <span>{label}</span>
                </button>
              ))}
              <div className="my-1 border-t border-border" />
            </>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate({ to: '/user-groups' });
            }}
            className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          >
            <UsersRound className="size-4" />
            <span>나만의 사용자 그룹</span>
          </button>
          {user.role === 'admin' || user.authProvider === 'local' ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setChangePasswordOpen(true);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-foreground-muted hover:bg-surface-muted hover:text-foreground"
            >
              <KeyRound className="size-4" />
              <span>비밀번호 변경</span>
            </button>
          ) : null}
          {user.role === 'admin' ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate({ to: '/admin' });
              }}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-foreground-muted hover:bg-surface-muted hover:text-foreground"
            >
              <Shield className="size-4" />
              <span>관리자</span>
            </button>
          ) : null}
          {user.apiRelayEnabled && user.llmApiRelayEnabledGlobal ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate({ to: '/api-keys' });
              }}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-foreground-muted hover:bg-surface-muted hover:text-foreground"
            >
              <Cable className="size-4" />
              <span>API 관리</span>
            </button>
          ) : null}
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          >
            <LogOut className="size-4" />
            <span>로그아웃</span>
          </button>
          {buildInfo.current ? (
            <>
              <div className="my-1 border-t border-border" />
              <div
                className="px-3 py-1.5 text-[11px] text-foreground-muted font-mono select-text"
                title={buildInfo.current.builtAt ?? ''}
              >
                v{buildInfo.current.version} · {buildInfo.current.commit}
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </div>
  );
}
